import { Component, computed, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RaceClassComponent } from './race-class.component';
import { RaceSessionService } from '../timing-tower/race-session.service';
import { formatTimespanShort } from './time-formatter';
import { Subscription } from 'rxjs';
import { IRaceClass } from '../timing-tower/IRaceTiming';

@Component({
	selector: 'app-race-info-bar',
	standalone: true,
	imports: [
		CommonModule,
		RaceClassComponent
	],
	styleUrl: './race-info-bar.component.css',
	template: `
		<div class="race-info-toolbar ps-3 pe-2 d-flex gap-2 justify-content-between">
			<ng-container *ngIf="RaceSessionService.raceHappening()">
				<div class="info-container">
					<div class="d-flex align-items-center gap-2" *ngIf="RaceSessionService.raceInfo$ | async as raceInfo">
						@if (raceInfo.champShortName) {
							<span class="min-w-0 text-truncate">{{ raceInfo.champShortName }}<span class="d-none d-sm-inline"> | {{ raceInfo.eventShortName }}</span></span>
						}
						@if (raceStatus()?.currentFlag) {
							<div
								class="flag-icon"
								[ngClass]="{'flag-finish':  raceStatus()?.currentFlag === 'FINISH' || raceStatus()?.isFinished }"
								[ngStyle]="{ 'background-color': !raceStatus()?.isFinished && raceStatus()?.currentFlag !== 'FINISH' ? flagColors[raceStatus()?.currentFlag] : undefined }"
								[title]="raceStatus()?.isFinished ? 'Finished' : 'Current Flag - ' + (raceStatus()?.currentFlag || 'No Flag')">
							</div>
						}
					</div>
					<div class="d-flex align-items-center gap-2 timing-info">
						@if(RaceSessionService.raceHappening()) {
							<div class="d-none d-sm-block">
								<span>Elapsed: {{ timeStatus().elapsedTime }}</span>
							</div>
							<div>
								<span>Remaining: {{ timeStatus().remainingTime }}</span>
							</div>
						}
					</div>
				</div>
				@if(!hideClasses && classes?.length) {
					<app-race-class [classes]="classes" (selectionChange)="onClassSelectionChange($event)"></app-race-class>
				}
			</ng-container>
		</div>
	`
})
export class RaceInfoBarComponent {
	@Input() public hideClasses = false;
	@Output() public classSelectionChanged = new EventEmitter<string[]>();

	public tick = signal(0);
	public RaceSessionService: RaceSessionService = inject(RaceSessionService);
	private subscription = new Subscription();
	public raceStatus = signal<any>({});
	public classes: IRaceClass[] = []


	public timeStatus = computed(() => {
		this.tick(); // force recomputation every second

		const status = this.raceStatus();
		const start = status?.sessionStartTime;
		const total = status?.finalTime;
		const isFinished = status?.isFinished;

		if (isFinished || !start || !total) {
			return {
				elapsedTime: '00:00:00',
				remainingTime: '00:00:00'
			};
		}

		const now = Date.now();
		const elapsedMs = Math.floor(now - start);
		const remainingMs = Math.max((total * 1000) - elapsedMs, 0);

		return {
			elapsedTime: formatTimespanShort(elapsedMs),
			remainingTime: formatTimespanShort(remainingMs)
		};
	});

	public flagColors: { [key: string]: string; } = {
		RED: '#FF0000',
		GREEN: '#00FF00',
		YELLOW: '#FFFF00',
		FULL_YELLOW: '#FFD700',
		SAFETY_CAR: '#FFA500',
		VIRTUAL_SAFETY_CAR: '#8A2BE2',
		CODE_60: '#800080',
		FLAG_INFO: '#CCCCCC'
	};

	public ngOnInit(): void {
		setInterval(() => this.tick.update(v => v + 1), 1000);
		this.subscription.add(
			this.RaceSessionService.raceStatus$.subscribe((status: any) => {
				this.raceStatus.set(status);
			})
		);
		this.subscription.add(
			this.RaceSessionService.raceClasses$.subscribe((classes: IRaceClass[]) => {
				this.classes = classes;
			})
		);
	}

	public onClassSelectionChange(selectedClasses: string[]): void {
		this.classSelectionChanged.emit(selectedClasses);
	}

	public ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
